import React, { useEffect, useState } from "react";

type Website = { label: string; href: string; imageUrl?: string };

type Props = {
  onBack?: () => void;
  sites?: Website[];
};

const DEFAULT_SITES: Website[] = [
  { label: "CodePlant", href: "https://codeplant.co.za" },
  { label: "GitHub", href: "https://github.com/seeward" },
];

const WebsitesList: React.FC<Props> = ({ onBack, sites }) => {
  const [items, setItems] = useState<Website[]>(sites ?? DEFAULT_SITES);

  useEffect(() => {
    // If consumer passed explicit sites, don't fetch
    if (sites && Array.isArray(sites)) {
      setItems(sites);
      return;
    }
    let aborted = false;
    (async () => {
      try {
        const res = await fetch("/api/links", { headers: { accept: "application/json" } });
        if (!res.ok) return; // keep defaults
        const data = await res.json();
        const arr = data && Array.isArray(data.links) ? data.links : [];
        if (!aborted && arr.length > 0) setItems(arr);
      } catch {
        // swallow: keep defaults
      }
    })();
    return () => {
      aborted = true;
    };
  }, [sites]);

  return (
    <section aria-label="Websites" className="websites-brutalist">
      <header className="panel-header">
        <h2 style={{ margin: 0 }}>Websites</h2>
        {onBack && (
          <button className="btn" onClick={onBack} aria-label="Back to blog">
            ← Back
          </button>
        )}
      </header>
      <ul className="site-list">
        {items.map((s) => (
          <li key={s.href} className="site-item">
            <a className="site-link" href={s.href} target="_blank" rel="noreferrer">
              <div className="site-info">
                {s.imageUrl && (
                  <div className="site-logo">
                    <img src={s.imageUrl} alt={`Logo of ${s.label}`} />
                  </div>
                )}
                <div className="site-text">
                  <span className="site-label">{s.label}</span>
                  <span className="site-url">{s.href}</span>
                </div>
              </div>
            </a>
          </li>
        ))}
      </ul>

      <style>
        {`
          /* Constrain websites section to match blog list width */
          .websites-brutalist .panel-header,
          .websites-brutalist .site-list {
            width: calc(100% - var(--post-list-narrow-by, 200px));
            margin-left: calc(0px - var(--post-list-shift-left, 0px));
          }

          .panel-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin: 0 0 12px;
          }
          .site-list {
            list-style: none;
            padding: 0;
            margin: 0;
            display: grid;
            gap: 10px;
          }
          .site-item { margin: 0; }
          .site-link {
            display: block;
            text-decoration: none;
            color: inherit;
            border: 6px solid #000;
            box-shadow: 10px 10px 0 #000;
            background: #fff;
            padding: 14px 16px;
            text-align: left;
          }
          .site-link:active { transform: translate(-2px, -2px); box-shadow: 12px 12px 0 #000; }
          .site-info {
            display: flex;
            align-items: center;
            gap: 12px;
          }
          .site-text {
            display: flex;
            flex-direction: column;
            flex: 1;
          }
          .site-label { font-weight: 900; text-transform: uppercase; letter-spacing: .5px; }
          .site-url { font-weight: 600; color: #444; word-break: break-all; }
          .site-logo { 
            flex-shrink: 0;
            width: 40px;
            height: 40px;
            border-radius: 4px;
            overflow: hidden;
            border: 1px solid #ddd;
          }
          .site-logo img { 
            width: 100%;
            height: 100%;
            display: block;
            object-fit: contain;
          }

          .btn {
            border: 3px solid #000;
            box-shadow: 3px 3px 0 #000;
            background: #fff;
            padding: 8px 12px;
            font-weight: 800;
            cursor: pointer;
          }

          @media (max-width: 760px) {
            .websites-brutalist .panel-header,
            .websites-brutalist .site-list { width: 100%; margin-left: 0; }
          }
        `}
      </style>
    </section>
  );
};

export default WebsitesList;
